import * as React from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { Button, Panel, PopupAction, Modal } from '@teamsnap/teamsnap-ui';
import Program from '../../models/program';
import * as GqlSeasons from '../../core/gql/queries/seasons';
import * as GqlPrograms from '../../core/gql/queries/programs';
import * as GqlDeleteProgramMutation from '../../core/gql/mutations/delete-program';
import * as GqlStaff from '../../core/gql/queries/show-all-staff';
import { reactiveFeedbackMessage } from '../../core/apollo-cache';
import { isAdminForProgram } from 'auth';
export interface Props {
  program: Program;
}

const ProgramCard: React.FunctionComponent<Props> = ({ program }) => {
  const [isDeleteProgramModalOpen, setIsDeleteProgramModalOpen] = React.useState(false);
  const seasonsQuery = useQuery(GqlSeasons.QUERY, { variables: { programId: program.id } });
  const history = useHistory();
  const mutationOptions = {
    refetchQueries: [{ query: GqlPrograms.QUERY, variables: { organizationId: program.organizationId } }],
  };
  const [deleteProgram] = useMutation(GqlDeleteProgramMutation.MUTATION, mutationOptions);

  const redirectToEditPage = (program: Program) => {
    history.push(`/organizations/${program.organizationId}/programs/${program.id}/edit`);
  };

  const sendProgramToDelete = async () => {
    try {
      await deleteProgram({
        variables: {
          id: program.id,
        },
      });
      const ARCHIVE_SUCCESS = `${program.name} has been deleted.`;
      reactiveFeedbackMessage({
        message: ARCHIVE_SUCCESS,
        isError: false,
        autoclose: true,
      });
    } catch (e) {
      const ARCHIVE_FAILURE = `Oops!  We couldn't delete ${program.name}. Please try again.`;
      reactiveFeedbackMessage({
        message: ARCHIVE_FAILURE,
        isError: true,
        autoclose: true,
      });
    }
  };

  let actions: any[] = [{ text: 'Edit', callback: () => redirectToEditPage(program) }];

  const queryStaff = useQuery(GqlStaff.QUERY, { variables: { organizationId: program.organizationId } });
  const staff = queryStaff?.data?.showOrganization?.staff || [];

  const programHasAdmin = isAdminForProgram(staff, program.id);

  if (seasonsQuery.data?.seasons?.length == 0 && !programHasAdmin) {
    actions = [...actions, { text: 'Delete', callback: () => setIsDeleteProgramModalOpen(true) }];
  }

  return (
    <Panel mods="program-card" key={`program-card-${program.id}`}>
      <div className="u-padMd u-xs-padSidesLg u-flex u-flexAlignItemsCenter u-flexJustifyBetween">
        <div className="Grid">
          <div className="Grid-cell u-xs-size1of2">
            <div className="u-spaceBottomSm u-fontSizeXl">
              <Link to={`/organizations/${program.organizationId}/programs/${program.id}`}>{program.name}</Link>
            </div>
            <p className="u-colorGrey">{program.description}</p>
          </div>
          <div className="Grid-cell u-xs-size1of2 u-textRight u-flex u-flexCol u-flexJustifyBetween">
            <div className="button-list">
              <PopupAction direction={['down', 'right']} text="..." actions={actions} popupStyle={{ width: '150px' }} />
            </div>
          </div>
        </div>
      </div>
      <Modal
        heading={<div className="u-textLeft">Delete Program?</div>}
        show={isDeleteProgramModalOpen}
        showClose={true}
        allowOverlayClose={true}
        closeFn={() => setIsDeleteProgramModalOpen(false)}
      >
        <p>{program.name} will be removed from your organization and all related information will be lost.</p>
        <div className="Modal-footer u-textRight u-xs-padTopLg">
          <Button mods="u-spaceRightLg" onClick={() => setIsDeleteProgramModalOpen(false)}>
            Cancel
          </Button>
          <Button className="Button u-bgNegative5 u-colorForeground" testId="deleteProgramButton" onClick={() => sendProgramToDelete()}>
            Delete
          </Button>
        </div>
      </Modal>
    </Panel>
  );
};
export default ProgramCard;
